import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { catchError } from "rxjs/operators";
import {
    BridgeTask,
    TaskFilterOptions,
    TaskSummary
} from "../../../models/bridge/bridge-task.model";
import { BridgeBaseService } from "./bridge-base.service";

/**
 * Service for 4Bridge Tasks API
 */
@Injectable({
    providedIn: 'root'
})
export class BridgeTaskService extends BridgeBaseService<BridgeTask> {

    constructor(httpClient: HttpClient) {
        super(httpClient, 'tasks');
    }

    /**
     * Get tasks with filters
     */
    public getTasks(filters?: TaskFilterOptions): Observable<BridgeTask[]> {
        let params = new HttpParams();

        if (filters) {
            if (filters.integrationId) {
                params = params.set('integrationId', filters.integrationId);
            }
            if (filters.status) {
                params = params.set('status', filters.status);
            }
            if (filters.startDate) {
                params = params.set('startDate', filters.startDate.toISOString());
            }
            if (filters.endDate) {
                params = params.set('endDate', filters.endDate.toISOString());
            }
            if (filters.page) {
                params = params.set('page', filters.page.toString());
            }
            if (filters.pageSize) {
                params = params.set('pageSize', filters.pageSize.toString());
            }
        }

        return this.httpClient
            .get<BridgeTask[]>(`${this.baseUrl}/${this.endpoint}`, { params })
            .pipe(
                catchError((error) => this.handleError(error))
            );
    }

    /**
     * Get task summary/statistics
     */
    public getSummary(): Observable<TaskSummary> {
        return this.httpClient
            .get<TaskSummary>(`${this.baseUrl}/${this.endpoint}/summary`)
            .pipe(
                catchError((error) => this.handleError(error))
            );
    }

    /**
     * Get tasks by integration ID
     */
    public getByIntegrationId(integrationId: string): Observable<BridgeTask[]> {
        return this.getTasks({ integrationId });
    }

    /**
     * Cancel a running task
     */
    public cancelTask(id: string): Observable<BridgeTask> {
        return this.httpClient
            .post<BridgeTask>(`${this.baseUrl}/${this.endpoint}/${id}/cancel`, {})
            .pipe(
                catchError((error) => this.handleError(error))
            );
    }

    /**
     * Retry a failed task
     */
    public retryTask(id: string): Observable<BridgeTask> {
        return this.httpClient
            .post<BridgeTask>(`${this.baseUrl}/${this.endpoint}/${id}/retry`, {})
            .pipe(
                catchError((error) => this.handleError(error))
            );
    }

    /**
     * Run a task now (trigger sync)
     */
    public runTask(id: string): Observable<any> {
        return this.httpClient
            .post(`${this.baseUrl}/${this.endpoint}/${id}/run`, {})
            .pipe(
                catchError((error) => this.handleError(error))
            );
    }

    /**
     * Create a new task
     */
    public createTask(task: Partial<BridgeTask>): Observable<BridgeTask> {
        return this.create(task);
    }

    /**
     * Update an existing task
     */
    public updateTask(id: string, task: Partial<BridgeTask>): Observable<BridgeTask> {
        return this.update(id, task);
    }
}
